import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, Button, Typography, Paper, Alert, CircularProgress, InputAdornment, IconButton, MenuItem } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { login, completeNewPassword, forgotPassword, confirmForgotPassword, getPublicSchools } from '../services/api';
import { useSchool } from '../contexts/SchoolContext';

export default function Login() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [schoolDomain, setSchoolDomain] = useState('');
  const [publicSchools, setPublicSchools] = useState([]);
  const isUsernameLogin = identifier && !identifier.includes('@');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [cognitoUser, setCognitoUser] = useState(null);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { school, loading: schoolLoading } = useSchool();
  const nav = useNavigate();

  React.useEffect(() => {
    // Retry until config.apiUrl is available (loaded async at startup)
    const load = () => {
      getPublicSchools().then(setPublicSchools).catch(() => setTimeout(load, 200));
    };
    load();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // Always clear any stale schoolId before attempting login
    localStorage.removeItem('schoolId');
    try {
      // If username login, always resolve schoolId from domain
      if (isUsernameLogin) {
        if (!schoolDomain) { setError('Please select your school'); setLoading(false); return; }
        const s = publicSchools.find(s => s.domain === schoolDomain);
        if (!s?.id) { setError('School not found'); setLoading(false); return; }
        localStorage.setItem('schoolId', s.id);
      }
      const result = await login(identifier, password);
      if (result.newPasswordRequired) {
        setCognitoUser(result.user);
        setNeedsNewPassword(true);
      } else {
        const user = JSON.parse(localStorage.getItem('user'));
        if (user) {
          nav('/');
        } else {
          setError('Login succeeded but user data not loaded. Please try again.');
        }
      }
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await completeNewPassword(cognitoUser, newPassword);
      nav('/');
    } catch (err) {
      setError(err.message || 'Password change failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(''); setMsg(''); setLoading(true);
    try {
      await forgotPassword(resetEmail);
      setCodeSent(true);
      setMsg('Verification code sent to your email');
    } catch (err) {
      setError(err.message || 'Failed to send reset code');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(''); setMsg(''); setLoading(true);
    try {
      await confirmForgotPassword(resetEmail, resetCode, newPassword);
      setMsg('Password reset successfully. You can now sign in.');
      setForgotMode(false); setCodeSent(false);
      setResetCode(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      setError(err.message || 'Password reset failed');
    } finally { setLoading(false); }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh"
      sx={{ pb: 6 }}>
      <Paper sx={{ p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 400, mx: 2, backdropFilter: 'blur(8px)', backgroundColor: 'rgba(30,30,30,0.92)' }}>
        <Box display="flex" justifyContent="center" mb={2}>
          {schoolLoading
            ? <CircularProgress size={36} sx={{ my: 2 }} />
            : <img src={school?.logoUrl || '/assets/logo-light.png'} alt={school?.name || 'Soapbox'} style={{ width: '90%', height: 'auto' }} />}
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {msg && <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert>}

        {forgotMode ? (
          !codeSent ? (
            <form onSubmit={handleForgotPassword}>
              <Typography variant="body2" color="text.secondary" mb={2}>Enter your email to receive a password reset code.</Typography>
              <TextField fullWidth label="Email" margin="normal" value={resetEmail} onChange={e => setResetEmail(e.target.value.toLowerCase())} />
              <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }} disabled={loading || !resetEmail}>
                {loading ? <CircularProgress size={24} /> : 'Send Reset Code'}
              </Button>
              <Button fullWidth sx={{ mt: 1 }} onClick={() => { setForgotMode(false); setError(''); setMsg(''); }}>Back to Sign In</Button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword}>
              <Typography variant="body2" color="text.secondary" mb={2}>Enter the code sent to your email and your new password.</Typography>
              <TextField fullWidth label="Verification Code" margin="normal" value={resetCode} onChange={e => setResetCode(e.target.value)} />
              <TextField fullWidth label="New Password" type={showPassword ? 'text' : 'password'} margin="normal" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} edge="end">{showPassword ? <Visibility /> : <VisibilityOff />}</IconButton></InputAdornment> }} />
              <TextField fullWidth label="Confirm Password" type={showPassword ? 'text' : 'password'} margin="normal" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
                error={!!confirmPassword && confirmPassword !== newPassword}
                helperText={confirmPassword && confirmPassword !== newPassword ? 'Passwords do not match' : ''} />
              <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }} disabled={loading || !resetCode || !newPassword || newPassword !== confirmPassword}>
                {loading ? <CircularProgress size={24} /> : 'Reset Password'}
              </Button>
              <Button fullWidth sx={{ mt: 1 }} onClick={() => { setForgotMode(false); setCodeSent(false); setError(''); setMsg(''); }}>Back to Sign In</Button>
            </form>
          )
        ) : !needsNewPassword ? (
          <form onSubmit={handleLogin}>
            <TextField fullWidth label="Username or Email" margin="normal" value={identifier} onChange={e => setIdentifier(e.target.value.toLowerCase())} />
            {isUsernameLogin && (
              <TextField select fullWidth label="School" margin="normal" value={schoolDomain}
                onChange={e => setSchoolDomain(e.target.value)}>
                {publicSchools.map(s => (
                  <MenuItem key={s.id} value={s.domain}>{s.name}</MenuItem>
                ))}
              </TextField>
            )}
            <TextField fullWidth label="Password" type={showPassword ? 'text' : 'password'} margin="normal" value={password} onChange={e => setPassword(e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} edge="end">{showPassword ? <Visibility /> : <VisibilityOff />}</IconButton></InputAdornment> }} />
            <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
            <Button fullWidth sx={{ mt: 1, textTransform: 'none' }} size="small" onClick={() => { setForgotMode(true); setResetEmail(identifier.includes('@') ? identifier : ''); setError(''); setMsg(''); }}>
              Forgot password?
            </Button>
          </form>
        ) : (
          <form onSubmit={handleNewPassword}>
            <Alert severity="info" sx={{ mb: 2 }}>Please set a new password</Alert>
            <TextField fullWidth label="New Password" type={showPassword ? 'text' : 'password'} margin="normal" value={newPassword} onChange={e => setNewPassword(e.target.value)}
              InputProps={{ endAdornment: <InputAdornment position="end"><IconButton onClick={() => setShowPassword(!showPassword)} edge="end">{showPassword ? <Visibility /> : <VisibilityOff />}</IconButton></InputAdornment> }} />
            <TextField fullWidth label="Confirm Password" type={showPassword ? 'text' : 'password'} margin="normal" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)}
              error={!!confirmPassword && confirmPassword !== newPassword}
              helperText={confirmPassword && confirmPassword !== newPassword ? 'Passwords do not match' : ''} />
            <Button fullWidth variant="contained" type="submit" sx={{ mt: 2 }} disabled={loading || !newPassword || newPassword !== confirmPassword}>
              {loading ? <CircularProgress size={24} /> : 'Set Password'}
            </Button>
          </form>
        )}
      </Paper>
      <Box component="footer" sx={{
        position: 'fixed', bottom: 0, left: 0, right: 0, py: 1, px: 2,
        backgroundColor: '#2a2a2a', borderTop: '1px solid rgba(255,255,255,0.08)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        flexWrap: 'wrap', gap: { xs: 0.5, sm: 1 }, zIndex: 1200,
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>Powered by Soapbox LLC</Typography>
        <img src="/assets/icon-footer.png" alt="Soapbox" style={{ height: 36 }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
          © {new Date().getFullYear()} All rights reserved.
        </Typography>
      </Box>
    </Box>
  );
}
